/**
 * Вход и восстановление пароля. Макет data/Вход.dc.html.
 *
 * §4.1 задаёт жёсткие рамки:
 *   — только email и пароль, поля логина нет;
 *   — «Запомнить меня» отсутствует и в интерфейсе, и в коде;
 *   — ошибки разделены: неверные данные, блокировка, деактивация,
 *     лимит попыток и сеть — это разные сообщения, а не одно «ошибка входа»;
 *   — восстановление не раскрывает, существует ли учётная запись.
 *
 * Ссылка из письма сброса ведёт сюда же — /login?reset=<токен>: отдельного
 * маршрута для нового пароля в §3.4 нет.
 */

import { ErrorCode } from "@taskflow/contracts";
import { useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  getWsClient,
  sessionEstablished,
  setAccessToken,
  type WsError,
} from "@taskflow/client-core";
import { Button, FormField, Input, PasswordInput } from "@/shared/ui";

import {
  useConfirmPasswordResetMutation,
  useLoginMutation,
  useRequestPasswordResetMutation,
} from "./api";

/** §4.1: каждая причина отказа объясняется отдельно. */
function loginErrorMessage(error: WsError): string {
  switch (error.data.code) {
    case ErrorCode.VALIDATION_ERROR:
      return "Проверьте адрес почты и пароль";
    case ErrorCode.AUTH_REQUIRED:
      return "Неверная почта или пароль";
    case ErrorCode.FORBIDDEN:
      return "Учётная запись заблокирована. Обратитесь к администратору";
    case ErrorCode.NOT_FOUND:
      return "Учётная запись деактивирована";
    case ErrorCode.RATE_LIMITED:
      return "Слишком много попыток входа. Повторите через несколько минут";
    case ErrorCode.INTERNAL_ERROR:
      return "Сервер недоступен. Проверьте соединение и повторите";
    default:
      return error.data.message;
  }
}

export function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const recovering = searchParams.get("recover") !== null;
  const resetToken = searchParams.get("reset");

  const status = useAppSelector((state) => state.auth.status);
  const sessionLostReason = useAppSelector((state) => state.auth.sessionLostReason);
  const location = useLocation();

  if (status === "authenticated") {
    const from = (location.state as { from?: { pathname: string } } | null)?.from;
    return <Navigate to={from?.pathname ?? "/tasks"} replace />;
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-5">
      <div className="w-full max-w-[400px] rounded-lg border border-line bg-surface p-8 shadow-sm max-sm:p-5">
        <h1 className="text-[19px] font-extrabold tracking-tight text-fg">mytaskflow</h1>

        {sessionLostReason !== null && !recovering && (
          <p
            role="status"
            className="mt-3.5 rounded-md bg-warning-soft px-3 py-2 text-xs font-semibold text-warning"
          >
            {sessionLostReason}
          </p>
        )}

        {resetToken !== null && resetToken !== "" ? (
          <NewPasswordForm
            token={resetToken}
            onDone={() => setSearchParams({}, { replace: true })}
          />
        ) : recovering ? (
          <RecoverForm onBack={() => setSearchParams({}, { replace: true })} />
        ) : (
          <LoginForm onRecover={() => setSearchParams({ recover: "" }, { replace: true })} />
        )}
      </div>
    </div>
  );
}

function LoginForm({ onRecover }: { onRecover: () => void }) {
  const dispatch = useAppDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    // §4.1: двойная отправка предотвращается — кнопка блокируется запросом.
    if (isLoading) return;
    setError(null);

    try {
      // trim и lowercase для сравнения — §4.1.
      const session = await login({
        email: email.trim().toLowerCase(),
        password,
      }).unwrap();

      setAccessToken(session.accessToken);
      dispatch(sessionEstablished({ user: session.user, permissions: session.permissions }));
      getWsClient().connect();
    } catch (raw) {
      setError(loginErrorMessage(raw as WsError));
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <h2 className="mt-5 text-xl font-extrabold text-fg">Вход</h2>
      <p className="mt-1 mb-4 text-[13px] text-muted">
        Введите почту и пароль, выданные администратором.
      </p>

      <div className="flex flex-col gap-3.5">
        <FormField label="Электронная почта" required>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              invalid={error !== null}
            />
          )}
        </FormField>

        <FormField label="Пароль" required>
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={error !== null}
            />
          )}
        </FormField>
      </div>

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-danger">
          {error}
        </p>
      )}

      {/* Кнопки «Запомнить меня» здесь нет и быть не должно — §4.1. */}
      <Button type="submit" block loading={isLoading} className="mt-4">
        Войти
      </Button>

      <button
        type="button"
        onClick={onRecover}
        className="mt-3.5 w-full text-center text-[13px] font-semibold text-accent hover:underline"
      >
        Забыли пароль?
      </button>
    </form>
  );
}

function RecoverForm({ onBack }: { onBack: () => void }) {
  const [request, { isLoading }] = useRequestPasswordResetMutation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    setError(null);

    try {
      await request({ email: email.trim().toLowerCase() }).unwrap();
      setSent(true);
    } catch (raw) {
      const wsError = raw as WsError;
      // Даже отказ по лимиту не должен подсказывать, есть ли такая почта.
      setError(
        wsError.data.code === ErrorCode.RATE_LIMITED
          ? "Слишком много запросов. Повторите позже"
          : "Не удалось отправить письмо. Повторите позже",
      );
    }
  };

  if (sent) {
    return (
      <div>
        <h2 className="mt-5 text-xl font-extrabold text-fg">Восстановление пароля</h2>
        {/* §4.3: ответ нейтральный — «если такой адрес существует». */}
        <p className="mt-3.5 mb-4 text-[13px] text-muted">
          Если такой адрес зарегистрирован, на него отправлена ссылка для смены пароля.
          Ссылка действует ограниченное время.
        </p>
        <Button variant="secondary" block onClick={onBack}>
          Вернуться ко входу
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <h2 className="mt-5 text-xl font-extrabold text-fg">Восстановление пароля</h2>
      <p className="mt-1 mb-4 text-[13px] text-muted">
        Укажите почту — отправим ссылку для смены пароля.
      </p>

      <FormField label="Электронная почта" required>
        {(props) => (
          <Input
            {...props}
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </FormField>

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-danger">
          {error}
        </p>
      )}

      <Button type="submit" block loading={isLoading} className="mt-4">
        Отправить ссылку
      </Button>
      <Button variant="ghost" block onClick={onBack} className="mt-2">
        Вернуться ко входу
      </Button>
    </form>
  );
}

function NewPasswordForm({ token, onDone }: { token: string; onDone: () => void }) {
  const [confirm, { isLoading }] = useConfirmPasswordResetMutation();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    if (password.length < 12) {
      setError("Пароль должен быть не короче 12 символов");
      return;
    }
    if (password !== repeat) {
      setError("Пароли не совпадают");
      return;
    }
    setError(null);
    try {
      await confirm({ token, password }).unwrap();
      setDone(true);
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  if (done) {
    return (
      <div>
        <h2 className="mt-5 text-xl font-extrabold text-fg">Пароль изменён</h2>
        <p className="mt-3.5 mb-4 text-[13px] text-muted">
          Войдите с новым паролем. Прежние сессии на других устройствах завершены.
        </p>
        <Button block onClick={onDone}>
          Перейти ко входу
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <h2 className="mt-5 text-xl font-extrabold text-fg">Новый пароль</h2>
      <p className="mt-1 mb-4 text-[13px] text-muted">Задайте новый пароль учётной записи.</p>

      <div className="flex flex-col gap-3.5">
        <FormField label="Новый пароль" required hint="Не короче 12 символов">
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </FormField>
        <FormField label="Повторите пароль" required>
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          )}
        </FormField>
      </div>

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-danger">
          {error}
        </p>
      )}

      <Button type="submit" block loading={isLoading} className="mt-4">
        Сохранить пароль
      </Button>
      <Button variant="ghost" block onClick={onDone} className="mt-2">
        Вернуться ко входу
      </Button>
    </form>
  );
}
